import { FC, useCallback, useEffect, useMemo } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  Alert,
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import __ from "src/utils/Translations";
import translateLabels from "src/utils/Translations/translateLabels";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import NumberField from "src/Pages/Company/Absences/Forms/NumberField/NumberField";
import CustomRadioGroup from "src/Pages/Company/Absences/Forms/CustomRadioGroup";
import {
  BillingCycle,
  BillingCycleConfigurationType,
  IAbsenceTypeForm,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import SubmitLimitations from "./SubmitLimitations";

const PatternsConfiguration = () => {
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const { control } = useFormContext<IAbsenceTypeForm>();

  const {
    fields: patternsArray,
    remove: removePattern,
    append: appendPattern,
  } = useFieldArray({
    control,
    name: `billingCycleConfiguration.patterns`,
    shouldUnregister: true,
    keyName: "key",
  });

  return (
    <>
      <Box>
        {patternsArray.map((field, index) => (
          <Stack
            key={field.key}
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              position: "relative",
              padding: "1rem 0",
            }}
          >
            <NumberField
              name={`billingCycleConfiguration.patterns.${index}.value`}
              defaultValue={1}
              min={1}
              disabled={!canWriteAbsencesConfiguration}
            />
            {!field.id && canWriteAbsencesConfiguration && (
              <IconButton onClick={() => removePattern(index)}>
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        ))}
        {canWriteAbsencesConfiguration && (
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() =>
              appendPattern({
                value: 1,
              })
            }
          >
            {__("Dodaj wzorzec")}
          </Button>
        )}
      </Box>
    </>
  );
};

const BillingCycleConfiguration: FC<{
  fullyEditable?: boolean;
  isStackUnusedTimeForNextBillingCycleEditable?: boolean;
  parentAbsenceType: string | null;
}> = ({
  fullyEditable,
  isStackUnusedTimeForNextBillingCycleEditable,
  parentAbsenceType,
}) => {
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  // Absence Type is editable is when it's new (undefined) or has enabled flag
  const isEditable =
    (fullyEditable === true || fullyEditable === undefined) &&
    !parentAbsenceType;
  // Stacking is editable is when Absence Type is new (undefined) or has enabled flag
  const isStackingEditable =
    isStackUnusedTimeForNextBillingCycleEditable === undefined
      ? true
      : isStackUnusedTimeForNextBillingCycleEditable;

  const { control, resetField, setValue } = useFormContext<IAbsenceTypeForm>();

  const chosenBillingCycle = useWatch({ name: "billingCycle" });

  const visible = chosenBillingCycle === BillingCycle.MONTHLY;

  const billingCycleOptions = useMemo(
    () => [
      { label: "Szablon bez wymiaru", value: BillingCycle.NONE },
      {
        label: "Szablon z wymiarem",
        value: BillingCycle.MONTHLY,
      },
    ],
    []
  );

  useEffect(() => {
    if (chosenBillingCycle === BillingCycle.NONE) {
      // @ts-expect-error needs to clear the field by setValue and undefined, reset doesn't affect related fields
      setValue("billingCycleConfiguration.type", undefined);
      resetField(
        "billingCycleConfiguration.stackUnusedTimeForNextBillingCycle",
        {
          defaultValue: false,
        }
      );

      resetField("billingCycleConfiguration.patterns", {
        defaultValue: [],
      });

      resetField("configurationOptions.submitLimitations.prior");

      resetField("configurationOptions.submitLimitations.advance");
    }
  }, [chosenBillingCycle, resetField, setValue]);

  const findBillingCycleType = useCallback(
    (value: string) =>
      value
        ? billingCycleTypeOptions.find((o) => o.id === value) || null
        : null,
    []
  );

  return (
    <>
      <CustomRadioGroup
        id="billingCycle"
        name="billingCycle"
        label={__("Wymiar nieobecności")}
        options={translateLabels(billingCycleOptions)}
        disabled={!canWriteAbsencesConfiguration || !isEditable}
        dataTestId="absence-type-form-field__billing-cycle"
      />
      <Collapse in={visible}>
        <Box
          sx={{
            bgcolor: "grey.100",
            padding: "1rem 2rem",
            marginTop: "1rem",
            borderRadius: "0.5rem",
          }}
        >
          <Controller
            name="billingCycleConfiguration.type"
            control={control}
            render={({
              field: { value, onChange, ref },
              fieldState: { invalid, error },
            }) => (
              <Autocomplete
                fullWidth
                disablePortal
                options={translateLabels(billingCycleTypeOptions)}
                value={findBillingCycleType(value!)}
                onChange={(_event, newValue) => onChange(newValue?.id)}
                disabled={!canWriteAbsencesConfiguration || !isEditable}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    label={__("Rozliczanie wymiaru")}
                    margin="normal"
                    error={invalid}
                    helperText={error?.message}
                    inputRef={ref}
                    data-testid="absence-type-form-field__billing-cycle-type"
                  />
                )}
              />
            )}
          />
          <Controller
            name="billingCycleConfiguration.stackUnusedTimeForNextBillingCycle"
            control={control}
            defaultValue={false}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!value}
                    onChange={onChange}
                    data-testid="absence-type-form-field__stackUnusedTimeForNextBillingCycle"
                  />
                }
                label={__("Przenieś zaległe dni na następny okres")}
                disabled={!canWriteAbsencesConfiguration || !isStackingEditable}
                sx={{
                  ".MuiFormControlLabel-label": {
                    fontWeight: "700",
                  },
                }}
              />
            )}
          />
          {!isStackingEditable && (
            <Alert severity="info">
              {__(
                "Brak możliwości zmiany switcha, ze względu na ustawienia wprowadzone w powiązanym szablonem wniosku."
              )}
            </Alert>
          )}
          <Typography variant="body2" sx={{ marginTop: "12px" }}>
            {__(
              "Podaj wzorce naliczania wymiaru nieobecności. Wzorce nie powinny się powtarzać."
            )}
          </Typography>
          <PatternsConfiguration />
        </Box>
      </Collapse>
      <Alert severity="info" sx={{ mt: "1rem" }}>
        {__(
          "Po pierwszym złożeniu wniosku lub przypisaniu wymiarów do pracowników, nie można zmienić ustawień wymiaru."
        )}
      </Alert>
      <Divider sx={{ my: "1rem" }} />
      <SubmitLimitations />
    </>
  );
};

const billingCycleTypeOptions = [
  { id: BillingCycleConfigurationType.Yearly, label: "Rocznie" },
  { id: BillingCycleConfigurationType.Monthly, label: "Miesięcznie" },
];

export default BillingCycleConfiguration;
